import {
	DEFAULT_BACKGROUND_CONFIG,
	ADAPTIVE_COLOR_BACKGROUND_CONFIGS,
	ADAPTIVE_GRADIENT_BACKGROUND_CONFIGS,
	NO_BACKGROUND_CONFIG,
	ADAPTIVE_IMAGE_BACKGROUND_CONFIG,
} from '@jira/platform__project-theme-tokens/src/constants.tsx';

import type {
	ThemeSetting,
	GradientThemeName,
	ColorThemeName,
	AdaptiveGradientBackgroundConfig,
	AdaptiveColorBackgroundConfig,
	ImageBackgroundConfig,
	ImageThemeSetting,
} from '@jira/platform__project-theme-tokens/src/types.tsx';

const ALL_COLOR_BACKGROUND_CONFIGS = [...ADAPTIVE_COLOR_BACKGROUND_CONFIGS, NO_BACKGROUND_CONFIG];
const ALL_GRADIENT_BACKGROUND_CONFIGS = ADAPTIVE_GRADIENT_BACKGROUND_CONFIGS;

/**
 * This is a temporary class that will be used to transition between the old
 * and new theme systems. Once the old theme system is removed, this class
 * will be removed as well.
 */
class ThemeManager {
	gradients: Map<GradientThemeName, AdaptiveGradientBackgroundConfig> = new Map();

	gradientNames: Set<GradientThemeName> = new Set();

	colors: Map<ColorThemeName, AdaptiveColorBackgroundConfig> = new Map();

	colorNames: Set<ColorThemeName> = new Set();

	init(): void {
		ALL_GRADIENT_BACKGROUND_CONFIGS.forEach((theme) => {
			this.gradients.set(theme.name, theme);
			this.gradientNames.add(theme.name);
		});

		ALL_COLOR_BACKGROUND_CONFIGS.forEach((theme) => {
			this.colors.set(theme.name, theme);
			this.colorNames.add(theme.name);
		});
	}

	isColorTheme(themeName: string | null | undefined): themeName is ColorThemeName {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		return this.colorNames.has(themeName as ColorThemeName);
	}

	isGradientTheme(themeName: string | null | undefined): themeName is GradientThemeName {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		return this.gradientNames.has(themeName as GradientThemeName);
	}

	isImageTheme(themeSetting: ThemeSetting): themeSetting is ImageThemeSetting {
		return themeSetting.type === 'image';
	}

	isValidTheme = (themeSetting: ThemeSetting): boolean =>
		this.isColorTheme(themeSetting.value) ||
		this.isGradientTheme(themeSetting.value) ||
		this.isImageTheme(themeSetting);

	getGradient(themeName: GradientThemeName): AdaptiveGradientBackgroundConfig | undefined {
		return this.gradients.get(themeName);
	}

	getColor(themeName: ColorThemeName): AdaptiveColorBackgroundConfig | undefined {
		return this.colors.get(themeName);
	}

	/**
	 * Gets the theme meta for the given theme setting and version.
	 * This is used because we want to handle cases where users get flag
	 * variations during rollout that change what background is allowed.
	 * For example, if you have a new background color, but the flag is off,
	 * we want to revert back to the default background color instead.
	 * @param themeSetting the name setting with its type and value.
	 * @param enableImages determines if image backgrounds are enabled.
	 * @returns the meta information for the theme.
	 */

	get({
		themeSetting,
		enableImages,
	}: {
		themeSetting: ThemeSetting | null;
		enableImages: boolean;
	}): ImageBackgroundConfig | AdaptiveGradientBackgroundConfig | AdaptiveColorBackgroundConfig {
		if (!themeSetting) {
			return DEFAULT_BACKGROUND_CONFIG;
		}

		if (enableImages && themeSetting.type === 'image') {
			return ADAPTIVE_IMAGE_BACKGROUND_CONFIG;
		}

		if (this.isGradientTheme(themeSetting.value)) {
			return this.getGradient(themeSetting.value) ?? DEFAULT_BACKGROUND_CONFIG;
		}

		if (this.isColorTheme(themeSetting.value)) {
			return this.getColor(themeSetting.value) ?? DEFAULT_BACKGROUND_CONFIG;
		}

		return DEFAULT_BACKGROUND_CONFIG;
	}
}

export const themeManager: ThemeManager = new ThemeManager();
themeManager.init();

type BackgroundConfigs = {
	backgroundColorConfigs: typeof ADAPTIVE_COLOR_BACKGROUND_CONFIGS;
	gradientBackgroundConfigs: typeof ADAPTIVE_GRADIENT_BACKGROUND_CONFIGS;
};

export function getBackgroundConfigs(): BackgroundConfigs {
	return {
		backgroundColorConfigs: ADAPTIVE_COLOR_BACKGROUND_CONFIGS,
		gradientBackgroundConfigs: ADAPTIVE_GRADIENT_BACKGROUND_CONFIGS,
	};
}
